import React from "react";
import styled, { css } from "styled-components";
import { Icon } from "../Icon/Icon";
import { BUTTON_COLORS, COLOR_VARIATIONS } from "@/shared/theme/colorUtils";

/**
 * Visual and semantic treatment for an application button.
 *
 * - `default`: low-emphasis text action with a transparent background.
 * - `primary`: the main action for a view or form.
 * - `outline`: a secondary action that should remain visually prominent.
 * - `ghost`: a neutral bordered action.
 * - `danger`: a destructive or irreversible action.
 */
export type AppButtonVariant = "default" | "primary" | "outline" | "ghost" | "danger";

/** Button sizing. `large` uses the 16px label size for prominent actions. */
export type AppButtonSize = "default" | "large";

/**
 * Props for the shared application button.
 *
 * @example
 * <AppButton variant="primary" onClick={handleSubmit}>
 *   Save changes
 * </AppButton>
 */
export interface AppButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
	children?: React.ReactNode;
	/** Controls the button's visual emphasis and semantic intent. */
	variant?: AppButtonVariant;
	/** Controls the label size. Defaults to `default`. */
	size?: AppButtonSize;
	/** Optional leading icon, either an Icon component or a legacy SVG path. */
	icon?: React.ReactNode | string;
	/** Optional trailing icon, either an Icon component or a legacy SVG path. */
	iconRight?: React.ReactNode | string;
	/** Size applied to icons passed as strings. */
	iconSize?: number;
	/** Color applied to icons passed as strings. */
	iconColor?: string;
}

const StyledButton = styled.button<{ $variant: AppButtonVariant; $size: AppButtonSize }>`
	display: flex;
	align-items: center;
	justify-content: center;
	gap: 6px;
	border: none;
	border-radius: 999px;
	box-sizing: border-box;
	cursor: pointer;
	font-family: inherit;
	font-size: 16px;
	font-weight: 600;
	line-height: 1.5;
	white-space: nowrap;
	height: 40px;
	transition:
		background-color 0.2s ease-in-out,
		border-color 0.2s ease-in-out,
		color 0.2s ease-in-out,
		box-shadow 0.2s ease-in-out;

	&:focus-visible {
		outline: 3px solid ${BUTTON_COLORS.focusRing};
		outline-offset: 2px;
	}

	${(props) =>
		props.$variant === "default" &&
		css`
			background: transparent;
			color: ${BUTTON_COLORS.text};
			font-size: 14px;
			font-weight: 500;
			padding: 6px 12px;

			&:hover:not(:disabled) {
				background: ${BUTTON_COLORS.surface};
				box-shadow: 0 6px 14px rgba(43, 52, 89, 0.14);
			}

			&:active:not(:disabled) {
				background: ${BUTTON_COLORS.neutralActive};
				color: ${COLOR_VARIATIONS.pink.main};
			}
		`}

	${(props) =>
		props.$variant === "primary" &&
		css`
			background: ${COLOR_VARIATIONS.primary.main};
			color: ${BUTTON_COLORS.surface};
			font-size: 14px;
			padding: 8px 16px;

			&:hover:not(:disabled) {
				background: ${COLOR_VARIATIONS.primary.hover};
			}

			&:active:not(:disabled) {
				background: ${COLOR_VARIATIONS.primary.active};
			}
		`}

	${(props) =>
		props.$variant === "outline" &&
		css`
			padding: 8px 18px;
			border: 2px solid ${COLOR_VARIATIONS.primary.main};
			background: ${BUTTON_COLORS.surface};
			color: ${COLOR_VARIATIONS.primary.main};
			font-size: 14px;
			box-shadow: none;

			&:hover:not(:disabled) {
				border-color: ${COLOR_VARIATIONS.primary.hover};
				background: ${COLOR_VARIATIONS.primary.lighter};
				color: ${COLOR_VARIATIONS.primary.hover};
				box-shadow: inset 0 0 0 2px ${COLOR_VARIATIONS.primary.hover};
			}

			&:focus-visible:not(:disabled) {
				box-shadow: inset 0 0 0 2px ${COLOR_VARIATIONS.primary.main};
			}

			&:active:not(:disabled) {
				border-color: ${COLOR_VARIATIONS.primary.active};
				background: ${COLOR_VARIATIONS.primary.light};
				color: ${COLOR_VARIATIONS.primary.active};
			}
		`}

    ${(props) =>
		props.$variant === "ghost" &&
		css`
			background: transparent;
			border: 1px solid ${BUTTON_COLORS.neutralBorder};
			color: ${BUTTON_COLORS.neutralText};
			padding: 6px 12px;

			&:hover:not(:disabled) {
				background: ${BUTTON_COLORS.neutralHover};
				border-color: ${BUTTON_COLORS.neutralBorderHover};
			}

			&:active:not(:disabled) {
				background: ${BUTTON_COLORS.neutralActive};
			}
		`}

    ${(props) =>
		props.$variant === "danger" &&
		css`
			background: ${BUTTON_COLORS.dangerBackground};
			color: ${BUTTON_COLORS.danger};
			padding: 6px 12px;

			&:hover:not(:disabled) {
				background: ${BUTTON_COLORS.dangerHover};
			}

			&:active:not(:disabled) {
				background: ${BUTTON_COLORS.dangerActive};
			}
		`}

	&:disabled {
		border-color: ${BUTTON_COLORS.disabledBorder};
		background: ${BUTTON_COLORS.disabledBackground};
		color: ${BUTTON_COLORS.disabledText};
		cursor: not-allowed;
		box-shadow: none;
	}

	${(props) =>
		props.$size === "large" &&
		css`
			font-size: 16px;
		`}
`;

const RenderIcon = ({ icon, size = 19, color }: { icon: React.ReactNode | string; size: number; color?: string }) => {
	if (typeof icon === "string") {
		return <Icon src={icon} size={size} color={color} />;
	}
	return <>{icon}</>;
};

/**
 * Shared pill-shaped button used for primary, secondary, neutral, and destructive actions.
 *
 * Use a native `button` type explicitly when submitting or resetting a form; it defaults to
 * `button` to avoid accidental form submission.
 */
export const AppButton = React.forwardRef<HTMLButtonElement, AppButtonProps>(
	(
		{
			children,
			variant = "default",
			size = "default",
			icon,
			iconRight,
			iconSize = 20,
			iconColor,
			type = "button",
			...buttonProps
		},
		ref,
	) => (
		<StyledButton ref={ref} type={type} $variant={variant} $size={size} {...buttonProps}>
			{icon && <RenderIcon icon={icon} size={iconSize} color={iconColor} />}
			{children}
			{iconRight && <RenderIcon icon={iconRight} size={iconSize} color={iconColor} />}
		</StyledButton>
	),
);

AppButton.displayName = "AppButton";
